import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { COMPARE_IDS } from "../../../Components/Filters/CompareSelect/presets"
import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { DASHBOARD_SLICE_NAME } from "./dashboard.constants"
import {
  getAlertOverviewData,
  getAlertsAccountData,
  getAlertsBankData,
  getAlertsCardTypeData,
  getAlertsCountryData,
  getAlertsDescriptorData,
  getAlertsFlow,
  getAllBusinessAccountsList,
  getCreditsSummary,
  getDashboardStatistics,
  getUsersBySourceData
} from "./dashboard.thunks"

const initialState = {
  fetching: false,
  result: null,
  errors: false,

  filtersLastUpd: null,
  filters: {
    period: "27 days",
    compare: { mode: COMPARE_IDS.PREV_PERIOD },
    lastCompare: null,
    alertsType: "count",
    descriptors: "all",
    business_accounts: "all"
  },

  alertsOverviewData: null,
  alertsOverviewFetching: false,

  alertsByDescriptorData: null,
  alertsByDescriptorFetching: false,

  alertsByAccountData: null,
  alertsByAccountFetching: false,

  allBusinessAccountsListFetching: false,
  allBusinessAccountsList: [],

  alertsCountryData: null,
  alertsCountryFetching: false,

  alertsBankData: null,
  alertsBankFetching: false,

  alertsCardTypeData: null,
  alertsCardTypeFetching: false,

  usersBySourceData: null,
  usersBySourceFetching: false,

  statisticBlocksData: [],
  // Shared day axis for the statistics blocks; every block's `series` indexes into it.
  statisticLabels: [],

  // { nodes, links, truncated } - the alert source -> outcome flow behind the Sankey.
  alertsFlowData: null,
  alertsFlowFetching: false,

  // { kpis, triggers, by_source } behind the credits card.
  creditsSummaryData: null,
  creditsSummaryFetching: false
}

// Both breakdowns get their bar percentage the same way every other distribution table
// on this page does: the row's share of its own list's total.
const withRowPercentage = (breakdown) => ({
  ...breakdown,
  result: (breakdown?.result || []).map((el) => ({
    ...el,
    percentage: breakdown?.total_count ? (el.count / breakdown.total_count) * 100 : 0
  }))
})

const slice = createSlice({
  name: DASHBOARD_SLICE_NAME,
  initialState,
  reducers: {
    dashboardSetProp(state, action) {
      const { key, value } = action.payload
      if (key === "filters") {
        state.filtersLastUpd = new Date().getTime()
        state.filters = { ...state.filters, ...value }
      } else {
        state[key] = value
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialState)

      .addCase(getAlertOverviewData.pending, (state) => {
        state.alertsOverviewFetching = true
      })
      .addCase(getAlertOverviewData.fulfilled, (state, action) => {
        const data = action.payload
        const total = data?.result?.data?.reduce((partialSum, el) => partialSum + el, 0)
        const comparisonTotal = data?.comparison?.data?.reduce((s, el) => s + el, 0) ?? null
        state.alertsOverviewFetching = false
        state.alertsOverviewData = {
          ...data,
          total,
          comparison: data?.comparison ? { ...data.comparison, total: comparisonTotal } : null
        }
      })
      .addCase(getAlertOverviewData.rejected, (state) => {
        state.alertsOverviewFetching = false
      })

      .addCase(getAlertsDescriptorData.pending, (state) => {
        state.alertsByDescriptorFetching = true
      })
      .addCase(getAlertsDescriptorData.fulfilled, (state, action) => {
        state.alertsByDescriptorFetching = false
        state.alertsByDescriptorData = action.payload
      })
      .addCase(getAlertsDescriptorData.rejected, (state) => {
        state.alertsByDescriptorFetching = false
      })

      .addCase(getAlertsAccountData.pending, (state) => {
        state.alertsByAccountFetching = true
      })
      .addCase(getAlertsAccountData.fulfilled, (state, action) => {
        state.alertsByAccountFetching = false
        state.alertsByAccountData = action.payload
      })
      .addCase(getAlertsAccountData.rejected, (state) => {
        state.alertsByAccountFetching = false
      })

      .addCase(getAlertsCountryData.pending, (state) => {
        state.alertsCountryFetching = true
      })
      .addCase(getAlertsCountryData.fulfilled, (state, action) => {
        const data = action.payload
        const tableData =
          data?.labels?.map((label, idx) => ({
            label,
            count: data.result.count[idx],
            volume: data.result.volume[idx]
          })) || []
        state.alertsCountryFetching = false
        state.alertsCountryData = { ...data, tableData }
      })
      .addCase(getAlertsCountryData.rejected, (state) => {
        state.alertsCountryFetching = false
      })

      .addCase(getAlertsBankData.pending, (state) => {
        state.alertsBankFetching = true
      })
      .addCase(getAlertsBankData.fulfilled, (state, action) => {
        const data = action.payload
        const result = data.result.map(
          (el) => ({ ...el, percentage: (el.count / data.total_count) * 100 }) || 0
        )
        state.alertsBankFetching = false
        state.alertsBankData = { ...data, result }
      })
      .addCase(getAlertsBankData.rejected, (state) => {
        state.alertsBankFetching = false
      })

      .addCase(getAlertsCardTypeData.pending, (state) => {
        state.alertsCardTypeFetching = true
      })
      .addCase(getAlertsCardTypeData.fulfilled, (state, action) => {
        const data = action.payload
        const result = data.result.map(
          (el) => ({ ...el, percentage: (el.count / data.total_count) * 100 }) || 0
        )
        state.alertsCardTypeFetching = false
        state.alertsCardTypeData = { ...data, result }
      })
      .addCase(getAlertsCardTypeData.rejected, (state) => {
        state.alertsCardTypeFetching = false
      })

      .addCase(getUsersBySourceData.pending, (state) => {
        state.usersBySourceFetching = true
      })
      .addCase(getUsersBySourceData.fulfilled, (state, action) => {
        state.usersBySourceFetching = false
        state.usersBySourceData = action.payload
      })
      .addCase(getUsersBySourceData.rejected, (state) => {
        state.usersBySourceFetching = false
      })

      .addCase(getAllBusinessAccountsList.pending, (state) => {
        state.allBusinessAccountsListFetching = true
      })
      .addCase(getAllBusinessAccountsList.fulfilled, (state, action) => {
        const data = action.payload
        state.allBusinessAccountsListFetching = false
        state.allBusinessAccountsList = data?.business_accounts || data
      })
      .addCase(getAllBusinessAccountsList.rejected, (state) => {
        state.allBusinessAccountsListFetching = false
      })

      .addCase(getDashboardStatistics.pending, (state) => {
        state.fetching = true
      })
      .addCase(getDashboardStatistics.fulfilled, (state, action) => {
        const data = action.payload
        state.fetching = false
        state.statisticBlocksData = data?.data_blocks
        state.statisticLabels = data?.labels ?? []
      })
      .addCase(getDashboardStatistics.rejected, (state) => {
        state.fetching = false
      })

      .addCase(getAlertsFlow.pending, (state) => {
        state.alertsFlowFetching = true
      })
      .addCase(getAlertsFlow.fulfilled, (state, action) => {
        state.alertsFlowFetching = false
        state.alertsFlowData = action.payload
      })
      .addCase(getAlertsFlow.rejected, (state) => {
        state.alertsFlowFetching = false
        state.alertsFlowData = null
      })

      .addCase(getCreditsSummary.pending, (state) => {
        state.creditsSummaryFetching = true
      })
      .addCase(getCreditsSummary.fulfilled, (state, action) => {
        const data = action.payload
        state.creditsSummaryFetching = false
        state.creditsSummaryData = {
          ...data,
          triggers: withRowPercentage(data?.triggers),
          by_source: withRowPercentage(data?.by_source)
        }
      })
      .addCase(getCreditsSummary.rejected, (state) => {
        state.creditsSummaryFetching = false
        state.creditsSummaryData = null
      })
  }
})

export const dashboardActions = slice.actions

const PERSISTED_REDUCER_VERSION = 3

export const persistedReducer = persistReducer(
  {
    key: "dashboard",
    storage,
    whitelist: ["filters", "filtersLastUpd"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
